interface Profile {
  age: number;
  income: string;
  netWorth: string;
  savingsCapacity: string;
  goal: string;
  horizon: string;
  experience: string;
  lossReaction: string;
  esgInterest: string;
  knowledge: string;
}

interface Product {
  name: string;
  category: string;
  description: string;
  riskLevel: string;
  expectedReturn: string;
  allocation: string;
}

interface RecommendationResponse {
  profileSummary: string;
  products: Product[];
}

const API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

export const getFinancialRecommendations = async (profile: Profile): Promise<RecommendationResponse> => {
  try {
    if (!API_KEY) {
      // Mode simulation si pas de clé API
      return getSimulatedRecommendations(profile);
    }

    const prompt = `
Analyse le profil financier suivant et fournis des recommandations personnalisées:
- Âge: ${profile.age} ans
- Revenus: ${profile.income}
- Patrimoine: ${profile.netWorth}
- Capacité d'épargne: ${profile.savingsCapacity}
- Objectif: ${profile.goal}
- Horizon: ${profile.horizon}
- Expérience: ${profile.experience}
- Réaction aux pertes: ${profile.lossReaction}
- Intérêt ESG: ${profile.esgInterest}
- Niveau de connaissance: ${profile.knowledge}

Réponds au format JSON avec:
{
  "profileSummary": "résumé du profil en une phrase",
  "products": [
    {
      "name": "nom du produit",
      "category": "catégorie",
      "description": "description courte",
      "riskLevel": "faible/modéré/élevé",
      "expectedReturn": "rendement attendu",
      "allocation": "pourcentage recommandé"
    }
  ]
}

Recommande 3-5 produits financiers adaptés au profil français (PEA, assurance-vie, etc.).
`;

    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-pro:generateContent?key=${API_KEY}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [
            {
              parts: [{ text: prompt }]
            }
          ]
        })
      }
    );

    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }

    const data = await response.json();
    const text = data.candidates?.[0]?.content?.parts?.[0]?.text;
    
    if (!text) {
      throw new Error('Réponse vide de l\'API');
    }

    // Extraire le JSON de la réponse
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      throw new Error('Format de réponse invalide');
    }

    return JSON.parse(jsonMatch[0]);
  } catch (error) {
    console.error('Erreur API Gemini:', error);
    // Fallback vers des recommandations simulées
    return getSimulatedRecommendations(profile);
  }
};

const getSimulatedRecommendations = (profile: Profile): RecommendationResponse => {
  const recommendations: RecommendationResponse = {
    profileSummary: `Profil ${profile.knowledge} avec un horizon ${profile.horizon} et un objectif de ${profile.goal}`,
    products: []
  };

  // Produits disponibles
  const availableProducts = [
    {
      name: "Sukuk Fractionné",
      category: "Finance éthique",
      description: "Obligation éthique fractionnée offrant une alternative d'investissement responsable",
      riskLevel: "Faible à modéré",
      expectedReturn: "4-6% par an",
      allocation: "0%"
    },
    {
      name: "SCPI",
      category: "Immobilier",
      description: "Société Civile de Placement Immobilier pour revenus locatifs réguliers",
      riskLevel: "Modéré",
      expectedReturn: "4-5% par an",
      allocation: "0%"
    },
    {
      name: "GFI",
      category: "Forestier",
      description: "Groupement Forestier d'Investissement pour diversification écologique",
      riskLevel: "Modéré à élevé",
      expectedReturn: "3-5% par an + valorisation long terme",
      allocation: "0%"
    }
  ];

  // Logique de recommandation basée sur le profil
  let allocations = { sukuk: 0, scpi: 0, gfi: 0 };
  
  // Allocation de base selon le niveau de connaissance
  if (profile.knowledge === 'Débutant') {
    allocations.sukuk = 80;
    allocations.scpi = 20;
    allocations.gfi = 0;
  } else if (profile.knowledge === 'Intermédiaire') {
    allocations.sukuk = 40;
    allocations.scpi = 50;
    allocations.gfi = 10;
  } else { // Confirmé
    allocations.sukuk = 30;
    allocations.scpi = 40;
    allocations.gfi = 30;
  }

  // Ajustements selon l'objectif
  if (profile.goal === 'Préservation du capital') {
    allocations.sukuk = 70;
    allocations.scpi = 30;
    allocations.gfi = 0;
  } else if (profile.goal === 'Revenus réguliers') {
    allocations.sukuk = 30;
    allocations.scpi = 60;
    allocations.gfi = 10;
  } else if (profile.goal === 'Croissance à long terme') {
    allocations.sukuk = 20;
    allocations.scpi = 40;
    allocations.gfi = 40;
  }

  // Ajustements selon l'horizon
  if (profile.horizon === 'Plus de 10 ans') {
    allocations.gfi += 10;
    allocations.sukuk -= 10;
  } else if (profile.horizon === 'Moins de 3 ans') {
    allocations.sukuk += 20;
    allocations.gfi = Math.max(0, allocations.gfi - 20);
  }

  // Ajustements ESG
  if (profile.esgInterest === "C'est une priorité absolue") {
    allocations.gfi += 20;
    allocations.sukuk -= 10;
    allocations.scpi -= 10;
  } else if (profile.esgInterest === "C'est un facteur important") {
    allocations.gfi += 10;
    allocations.sukuk -= 5;
    allocations.scpi -= 5;
  }

  // Ajustements selon la capacité d'épargne
  if (profile.savingsCapacity === 'Plus de 1500 €') {
    allocations.gfi += 10;
    allocations.scpi += 5;
    allocations.sukuk -= 15;
  } else if (profile.savingsCapacity === 'Moins de 200 €') {
    allocations.sukuk += 20;
    allocations.scpi -= 10;
    allocations.gfi = Math.max(0, allocations.gfi - 10);
  }

  // Normaliser pour que le total soit 100%
  const total = allocations.sukuk + allocations.scpi + allocations.gfi;
  if (total > 0) {
    allocations.sukuk = Math.round((allocations.sukuk / total) * 100);
    allocations.scpi = Math.round((allocations.scpi / total) * 100);
    allocations.gfi = 100 - allocations.sukuk - allocations.scpi;
  }

  // Créer les produits avec leurs allocations
  const selectedProducts = [];
  
  if (allocations.sukuk > 0) {
    selectedProducts.push({
      ...availableProducts[0],
      allocation: `${allocations.sukuk}%`
    });
  }
  
  if (allocations.scpi > 0) {
    selectedProducts.push({
      ...availableProducts[1],
      allocation: `${allocations.scpi}%`
    });
  }
  
  if (allocations.gfi > 0) {
    selectedProducts.push({
      ...availableProducts[2],
      allocation: `${allocations.gfi}%`
    });
  }

  recommendations.products = selectedProducts;
  return recommendations;
};